<template>
  <div>
    <div class="d-flex align-items-center justify-content-between mb-1">
      <div class="d-flex align-items-center">
        <label class="mr-2 mb-0 font-small-3">Per Page</label>
        <b-form-select
          v-model="perPage"
          :options="perPageOptions"
          size="sm"
          class="w-auto"
          @change="onPerPageChange"
        />
        <label class="mb-0 font-small-3 ml-1">
          Total {{ totalCount }} entries
        </label>
      </div>
    </div>

    <b-table
      responsive
      :items="history"
      :fields="tableColumns"
      show-empty
      empty-text="No limit set history found"
      :busy="loading"
      small
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle mr-1" small />
          <strong>Loading...</strong>
        </div>
      </template>

      <template #cell(sl)="data">
        {{ offset + data.index + 1 }}
      </template>

      <template #cell(created_at)="data">
        {{ data.item.created_at | formatLocalDateTime }}
      </template>

      <template #cell(source)="data">
        {{ data.item.source || "—" }}
      </template>

      <template #cell(created_by_username)="data">
        {{ data.item.created_by_username || "—" }}
      </template>
    </b-table>

    <b-row class="mt-1">
      <b-col
        cols="12"
        sm="5"
        class="d-flex align-items-center justify-content-center justify-content-sm-start"
      >
        <span class="text-muted font-small-3">
          Showing {{ fromEntry }} to {{ toEntry }} of {{ totalCount }} entries
        </span>
      </b-col>
      <b-col
        cols="12"
        sm="7"
        class="d-flex align-items-center justify-content-center justify-content-sm-end"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="perPage"
          first-number
          last-number
          class="mb-0 mt-1 mt-sm-0"
          prev-class="prev-item"
          next-class="next-item"
          size="sm"
          @input="onPageChange"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="16" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="16" />
          </template>
        </b-pagination>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BTable,
  BPagination,
  BRow,
  BCol,
  BFormSelect,
  BSpinner,
} from "bootstrap-vue";
import api from "@/services/api";

export default {
  name: "LineLimitSetHistory",

  components: {
    BTable,
    BPagination,
    BRow,
    BCol,
    BFormSelect,
    BSpinner,
  },

  props: {
    lineId: {
      type: [Number, String],
      default: null,
    },
    customerId: {
      type: String,
      default: "",
    },
    endpoint: {
      type: String,
      required: true,
    },
    customerIdParam: {
      type: String,
      default: "customer_id",
    },
    showSourceColumn: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      history: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 20, 50],
    };
  },

  computed: {
    offset() {
      return (this.currentPage - 1) * this.perPage;
    },

    tableColumns() {
      const columns = [
        { key: "sl", label: "#", sortable: false },
        { key: "mobile_number", label: "Mobile Number", sortable: false },
        { key: "sip_number", label: "MiME Talk Number", sortable: false },
        { key: "previous_limit", label: "Previous Limit (Tk)", sortable: false },
        { key: "new_limit", label: "New Limit (Tk)", sortable: false },
      ];
      if (this.showSourceColumn) {
        columns.push({ key: "source", label: "Source", sortable: false });
      }
      columns.push(
        { key: "created_at", label: "Date", sortable: false },
        { key: "created_by_username", label: "Set By", sortable: false }
      );
      return columns;
    },

    fromEntry() {
      if (!this.totalCount) return 0;
      return this.offset + 1;
    },

    toEntry() {
      return Math.min(this.offset + this.history.length, this.totalCount);
    },
  },

  watch: {
    lineId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.resetState();
          this.fetchHistory();
        }
      },
    },
  },

  methods: {
    resetState() {
      this.history = [];
      this.totalCount = 0;
      this.currentPage = 1;
    },

    onPerPageChange() {
      this.currentPage = 1;
      this.fetchHistory();
    },

    onPageChange() {
      this.fetchHistory();
    },

    async fetchHistory() {
      if (!this.lineId) return;

      this.loading = true;
      try {
        const params = {
          line_id: this.lineId,
          limit: this.perPage,
          offset: this.offset,
        };
        if (this.customerId) {
          params[this.customerIdParam] = this.customerId;
        }

        const res = await api.get(this.endpoint, { params });
        this.history = res.data.results || [];
        this.totalCount = res.data.count || 0;
      } catch (error) {
        console.error("Limit set history fetch failed:", error);
        this.history = [];
        this.totalCount = 0;
        this.$bvToast.toast(
          error.response?.data?.message || "Failed to load limit set history",
          {
            title: "Error",
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
